import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { Skeleton } from './components/ui/skeleton';

// Lazy-loaded pages
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const BanksPage = React.lazy(() => import('./pages/BanksPage'));
const AgentsPage = React.lazy(() => import('./pages/AgentsPage'));
const TemplatesPage = React.lazy(() => import('./pages/TemplatesPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const PublicUploadPage = React.lazy(() => import('./pages/PublicUploadPage'));
const InvoiceViewPage = React.lazy(() => import('./pages/InvoiceViewPage'));
const PaymentsPage = React.lazy(() => import('./pages/PaymentsPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const StaffPage = React.lazy(() => import('./pages/StaffPage'));
const ChangePasswordPage = React.lazy(() => import('./pages/ChangePasswordPage'));
const CMSDirectoryPage = React.lazy(() => import('./pages/CMSDirectoryPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));

// Components
import Layout from './components/Layout';

// Standalone Full-Page Loading Spinner
function FullPageSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="fullpage-spinner">
      <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// Page loading skeleton
function PageSkeleton() {
  return (
    <div className="space-y-6 animate-fade-in" data-testid="route-skeleton">
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32" />
      </div>
      <div className="rounded-lg border bg-card">
        <div className="p-6 space-y-4">
          <Skeleton className="h-10 w-full max-w-sm" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    </div>
  );
}

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <FullPageSpinner />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Layout>
      <Suspense fallback={<PageSkeleton />}>
        {children}
      </Suspense>
    </Layout>
  );
}

// Admin Route wrapper
function AdminRoute({ children }) {
  const { user } = useAuth();

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Suspense fallback={<FullPageSpinner />}><LoginPage /></Suspense>} />
      <Route path="/upload/:token" element={<Suspense fallback={<FullPageSpinner />}><PublicUploadPage /></Suspense>} />

      {/* Protected Routes */}
      <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/banks" element={<ProtectedRoute><BanksPage /></ProtectedRoute>} />
      <Route path="/agents" element={<ProtectedRoute><AgentsPage /></ProtectedRoute>} />
      <Route path="/cms-directory" element={<ProtectedRoute><CMSDirectoryPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />

      {/* Legacy routes kept working + redirects */}
      <Route path="/certificates" element={<Navigate to="/billing" replace />} />
      <Route path="/invoices" element={<Navigate to="/billing" replace />} />
      <Route path="/invoices/generate" element={<Navigate to="/billing" replace />} />
      <Route path="/invoices/:id" element={<ProtectedRoute><InvoiceViewPage /></ProtectedRoute>} />
      <Route path="/payments" element={<ProtectedRoute><PaymentsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
      <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />

      {/* Admin Only Routes */}
      <Route path="/settings" element={<ProtectedRoute><AdminRoute><SettingsPage /></AdminRoute></ProtectedRoute>} />
      <Route path="/staff" element={<ProtectedRoute><AdminRoute><StaffPage /></AdminRoute></ProtectedRoute>} />

      {/* 404 — top-level so it works for both authed and unauthed users.
          NotFoundPage renders its own lightweight wrapper; never silently redirects. */}
      <Route path="*" element={<Suspense fallback={<FullPageSpinner />}><NotFoundPage /></Suspense>} />
    </Routes>
  );
}

function App() {
  // Unregister old service workers and purge stale caches to ensure fresh asset delivery
  React.useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      }).catch(() => {});
    }
    if ('caches' in window) {
      caches.keys().then((names) => {
        for (let name of names) {
          if (name.includes('v7') || name.includes('v6') || name.includes('v5') || name.includes('vajra-group-static')) {
            caches.delete(name);
          }
        }
      }).catch(() => {});
    }
  }, []);


  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                border: '1px solid hsl(var(--border))'
              }
            }}
          />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
